/**
 * Development Tools - PocketMocker Integration
 *
 * Provides in-browser HTTP mocking for frontend development without backend changes.
 * Only active in development mode.
 *
 * Usage:
 * - Toggle mock panel with keyboard shortcut (Ctrl+Shift+M)
 * - Click network requests in panel to create mocks
 * - Use template syntax for dynamic data (@guid, @name, @integer, etc.)
 *
 * @see https://github.com/tianchangNorth/pocket-mocker
 */

// Only import in development to avoid bundling in production
// Using any type to avoid Vite static analysis of the import
let pocketMock: any = null;

/**
 * Initialize development tools
 * Call this at app startup (main.tsx)
 */
export async function initDevTools(): Promise<void> {
  // Only run in development
  if (!import.meta.env.DEV) {
    return;
  }

  try {
    // Dynamic import to avoid production bundle
    const { pocketMock: pm } = await import('pocket-mocker');
    pocketMock = pm;

    // Initialize with default configuration
    pocketMock({
      // Panel configuration
      showPanel: false, // Start hidden, toggle with Ctrl+Shift+M
      position: 'bottom-right',

      // Debugging options
      logRequests: true,
    });

    console.log('🎭 PocketMocker initialized');
    console.log('   Toggle panel: Ctrl+Shift+M (or click bottom-right corner)');
    console.log('   Docs: https://github.com/tianchangNorth/pocket-mocker');

  } catch (error) {
    console.warn('PocketMocker not available:', error);
  }
}

/**
 * Programmatically add a mock rule
 * Useful for setting up test scenarios
 */
export async function addMock(config: {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  status?: number;
  delay?: number;
  response: unknown;
}): Promise<void> {
  if (!import.meta.env.DEV) {
    console.warn('addMock() only available in development');
    return;
  }

  // PocketMocker stores mocks in localStorage
  const existingMocks = JSON.parse(localStorage.getItem('pocket-mocker-rules') || '[]');
  existingMocks.push({
    ...config,
    method: config.method || 'GET',
    status: config.status || 200,
    enabled: true,
  });
  localStorage.setItem('pocket-mocker-rules', JSON.stringify(existingMocks));

  console.log(`🎭 Mock added: ${config.method || 'GET'} ${config.url}`);
}

/**
 * Clear all mock rules
 */
export function clearMocks(): void {
  if (!import.meta.env.DEV) return;
  localStorage.removeItem('pocket-mocker-rules');
  console.log('🎭 All mocks cleared');
}

/**
 * Pre-built mock scenarios for common testing needs
 */
export const mockScenarios = {
  /**
   * Simulate empty inventory state
   */
  emptyInventory: () => addMock({
    url: '/api/items-pg*',
    method: 'GET',
    response: { items: [], total: 0, page: 1, limit: 20 },
  }),

  /**
   * Simulate eBay API being unavailable
   */
  ebayDown: () => addMock({
    url: '/api/ebay/*',
    method: 'GET',
    status: 503,
    response: {
      error: 'EBAY_SERVICE_UNAVAILABLE',
      message: 'eBay API is temporarily unavailable',
    },
  }),

  /**
   * Simulate slow network (3 second delay)
   */
  slowNetwork: () => addMock({
    url: '/api/*',
    delay: 3000,
    response: null, // Pass through to real API
  }),

  /**
   * Simulate eBay rate limit
   */
  ebayRateLimit: () => addMock({
    url: '/api/ebay/*',
    status: 429,
    response: {
      error: 'RATE_LIMIT_EXCEEDED',
      message: 'Too many requests. Please wait before trying again.',
      retryAfter: 60,
    },
  }),

  /**
   * Simulate validation error on item save
   */
  validationError: () => addMock({
    url: '/api/items-pg/*',
    method: 'PATCH',
    status: 400,
    response: {
      error: 'VALIDATION_ERROR',
      details: [
        { field: 'title', message: 'Title is required' },
        { field: 'price', message: 'Price must be greater than 0' },
      ],
    },
  }),

  /**
   * Simulate unauthorized (expired session)
   */
  unauthorized: () => addMock({
    url: '/api/*',
    status: 401,
    response: {
      error: 'UNAUTHORIZED',
      message: 'Session expired. Please log in again.',
    },
  }),
};

// Expose to window for console access in development
if (import.meta.env.DEV) {
  (window as any).devTools = {
    addMock,
    clearMocks,
    mockScenarios,
  };
}
